import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { sortBy } from 'ramda'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import UpgradeIcon from '@mui/icons-material/Upgrade'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourseMigratableContent,
  fetchCourseV2Compliances,
  fetchPlanningCourseV2Details,
  fetchPlanningCourseV2Objectives,
  fetchPlanningCourseV2References,
  fetchPlanningCoursesV2,
  fetchPlanningCoursesV2Progress,
  fetchPlanningSections,
  fetchPlanningTemplates,
  selectCourseMigratableContentByCourseID,
  selectCourseV2ComplianceByCourseID,
  selectCourseV2Progress,
  selectCourseV2ProgressStatus,
  selectPlanningCourseV2DetailsByCourseID,
  selectPlanningCourseV2DetailsStatusByCourseID,
  selectPlanningCoursesV2,
  selectPlanningCoursesV2Status,
  fetchCourseV2CAPCEAccreditation,
  fetchCourseV2ANCCAccreditation,
  fetchCourseV2QuizThunk,
  fetchCourseV2QuizQuestionsThunk,
  createCourseV2QuizThunk,
  selectCourseV2CAPCEAccreditationByCourseID,
  selectCourseV2CAPCEAccreditationStatusByCourseID,
  selectCourseV2ANCCAccreditationByCourseID,
  selectCourseV2ANCCAccreditationStatusByCourseID,
  selectCourseV2QuizByCourseID,
  selectCourseV2QuizStatusByCourseID,
  selectCourseV2QuizQuestionsByCourseID,
  fetchCourseV2Videos,
  fetchCourseV2ShowNotes,
  selectCourseV2VideosByCourseID,
  selectCourseV2ShowNotesByCourseID,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { QuizBuilder } from 'src/v3/components/quiz/quiz-builder'
import CourseField from './fields/course-field'
import CourseCAPCEFieldEditable from './fields/course-capce-field-editable'
import CourseANCCFieldEditable from './fields/course-ancc-field-editable'
import CourseListWithProgress from './components/course-list-with-progress'
import CourseCreationModal from './components/course-creation-modal'
import CourseObjectivesManagement from './components/course-objectives-management'
import CourseReferencesManagement from './components/course-references-management'
import CourseInstructorsManagement from './components/course-instructors-management'
import MigrateTitleScreenModal from './components/migrate-title-screen-modal'
import CourseContentTab from './components/course-content-tab'
import MigrateQuizModal from './components/migrate-quiz-modal'
import MigrateAccreditationModal from './components/migrate-accreditation-modal'
import CourseCAPCECreationForm from './components/course-capce-creation-form'
import CourseANCCCreationForm from './components/course-ancc-creation-form'
import CoursePublishChecklist from './components/course-publish-checklist'
import PipelineFilterBar from '../../components/pipeline-filter-bar'
import BackfillVideoLengthsButton from '../../components/backfill-video-lengths-button'
import SelectItemHint from '../select-item-hint'

type WorkflowFilter =
  'all' | 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved'

type MigrationFilter = 'all' | 'needs-migration' | 'v2'

export default function CourseV2PlanningDashboard(): ReactElement {
  let dispatch = useAppDispatch()
  let theme = useTheme()

  let courses = useAppSelector(selectPlanningCoursesV2)
  let coursesStatus = useAppSelector(selectPlanningCoursesV2Status)
  let coursesProgress = useAppSelector(selectCourseV2Progress)
  let coursesProgressStatus = useAppSelector(selectCourseV2ProgressStatus)
  let [activeCourseID, setActiveCourseID] = useState<string>()
  let [courseFilter, setCourseFilter] =
    useState<WorkflowFilter>('needs-revision')
  let [migrationFilter, setMigrationFilter] = useState<MigrationFilter>('all')
  let [searchQuery, setSearchQuery] = useState('')
  let [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  let [activeTab, setActiveTab] = useState(0)
  let [createModalOpen, setCreateModalOpen] = useState(false)
  let [titleScreenModalOpen, setTitleScreenModalOpen] = useState(false)
  let [migrateQuizModalOpen, setMigrateQuizModalOpen] = useState(false)
  let [migrateAccreditationModalOpen, setMigrateAccreditationModalOpen] =
    useState(false)
  let [capceFormOpen, setCapceFormOpen] = useState(false)
  let [anccFormOpen, setAnccFormOpen] = useState(false)

  let activeCourseDetails = useAppSelector(
    selectPlanningCourseV2DetailsByCourseID(activeCourseID ?? ''),
  )
  let activeCourseDetailsStatus = useAppSelector(
    selectPlanningCourseV2DetailsStatusByCourseID(activeCourseID ?? ''),
  )
  let activeCourseCompliance = useAppSelector(
    selectCourseV2ComplianceByCourseID(activeCourseID ?? ''),
  )
  let activeMigratableContent = useAppSelector(
    selectCourseMigratableContentByCourseID(activeCourseID ?? ''),
  )
  let activeCourseCapce = useAppSelector(
    selectCourseV2CAPCEAccreditationByCourseID(activeCourseID ?? ''),
  )
  let activeCourseCapceStatus = useAppSelector(
    selectCourseV2CAPCEAccreditationStatusByCourseID(activeCourseID ?? ''),
  )
  let activeCourseAncc = useAppSelector(
    selectCourseV2ANCCAccreditationByCourseID(activeCourseID ?? ''),
  )
  let activeCourseAnccStatus = useAppSelector(
    selectCourseV2ANCCAccreditationStatusByCourseID(activeCourseID ?? ''),
  )
  let activeCourseQuiz = useAppSelector(
    selectCourseV2QuizByCourseID(activeCourseID ?? ''),
  )
  let activeCourseQuizStatus = useAppSelector(
    selectCourseV2QuizStatusByCourseID(activeCourseID ?? ''),
  )
  let activeCourseQuizQuestions = useAppSelector(
    selectCourseV2QuizQuestionsByCourseID(activeCourseID ?? ''),
  )
  let activeCourseVideos = useAppSelector(
    selectCourseV2VideosByCourseID(activeCourseID ?? ''),
  )
  let activeCourseShowNotes = useAppSelector(
    selectCourseV2ShowNotesByCourseID(activeCourseID ?? ''),
  )

  // Refetch every time this dashboard is landed on (mounted), rather than
  // only on first-ever load, so switching tabs and coming back shows fresh data.
  useEffect(() => {
    dispatch(fetchPlanningCoursesV2())
    dispatch(fetchCourseV2Compliances())
    dispatch(fetchPlanningCoursesV2Progress())
    dispatch(fetchPlanningSections())
    dispatch(fetchPlanningTemplates())
  }, [dispatch])

  let getWorkflowStatus = (courseId: string): WorkflowFilter => {
    let progress = coursesProgress[courseId]
    if (!progress) return 'in-planning'
    if ((progress.complianceStatus.rejected ?? 0) > 0) return 'needs-revision'
    if ((progress.dataCompleteness.percentComplete ?? 0) < 100)
      return 'in-planning'
    if ((progress.complianceStatus.percentApproved ?? 0) < 100)
      return 'ready-for-review'
    return 'approved'
  }

  let overviewStats = useMemo(
    () =>
      courses.reduce(
        (acc, c) => {
          let status = getWorkflowStatus(c.id)
          let missing =
            coursesProgress[c.id]?.dataCompleteness.missingFields?.length ?? 0
          return {
            needsRevision:
              acc.needsRevision + (status === 'needs-revision' ? 1 : 0),
            inPlanning: acc.inPlanning + (status === 'in-planning' ? 1 : 0),
            readyForReview:
              acc.readyForReview + (status === 'ready-for-review' ? 1 : 0),
            approved: acc.approved + (status === 'approved' ? 1 : 0),
            totalFieldsRemaining: acc.totalFieldsRemaining + missing,
          }
        },
        {
          needsRevision: 0,
          inPlanning: 0,
          readyForReview: 0,
          approved: 0,
          totalFieldsRemaining: 0,
        },
      ),
    [courses, coursesProgress],
  )

  let filteredCourses = useMemo(() => {
    let migrationBase =
      migrationFilter === 'needs-migration'
        ? courses.filter((c) => c.publishedVersion === 1)
        : migrationFilter === 'v2'
          ? courses.filter((c) => c.publishedVersion === 2)
          : courses
    let workflowBase =
      courseFilter === 'all'
        ? migrationBase
        : migrationBase.filter((c) => getWorkflowStatus(c.id) === courseFilter)
    let searchBase = searchQuery
      ? workflowBase.filter((c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : workflowBase
    return sortBy((c) => {
      let t = c.createdTime ? new Date(c.createdTime).getTime() : 0
      return sortDir === 'asc' ? -t : t
    }, searchBase)
  }, [
    courses,
    coursesProgress,
    courseFilter,
    migrationFilter,
    searchQuery,
    sortDir,
  ])

  // Always fetch fresh migratable content and CAPCE/ANCC/videos/show notes
  // when the active course changes — a course's compliance state can change
  // server-side between visits, so a stale cached copy isn't good enough.
  useEffect(() => {
    if (!activeCourseID) return
    dispatch(fetchCourseMigratableContent(activeCourseID))
    dispatch(fetchCourseV2CAPCEAccreditation(activeCourseID))
    dispatch(fetchCourseV2ANCCAccreditation(activeCourseID))
    dispatch(fetchCourseV2Videos(activeCourseID))
    dispatch(fetchCourseV2ShowNotes(activeCourseID))
  }, [activeCourseID, dispatch])

  // Single trigger for details: fetch on course switch (same freshness rule
  // as above) or when a mutation invalidates them ('uninitialized'). Keeping
  // both in one effect avoids a duplicate GET when switching back to a course
  // whose save resolved while it was inactive.
  let lastDetailsCourseIDRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!activeCourseID) {
      // Reset so deselect → reselect of the same course counts as a switch
      // eslint-disable-next-line fp/no-mutation
      lastDetailsCourseIDRef.current = undefined
      return
    }
    let courseChanged = lastDetailsCourseIDRef.current !== activeCourseID
    if (courseChanged || activeCourseDetailsStatus === 'uninitialized') {
      // eslint-disable-next-line fp/no-mutation
      lastDetailsCourseIDRef.current = activeCourseID
      dispatch(fetchPlanningCourseV2Details(activeCourseID))
    }
  }, [activeCourseID, activeCourseDetailsStatus, dispatch])

  // Fetch quiz when assessment tab is active
  useEffect(() => {
    if (activeCourseID && activeTab === 4) {
      dispatch(fetchCourseV2QuizThunk(activeCourseID))
    }
  }, [activeCourseID, activeTab, dispatch])

  // Fetch quiz questions once quiz is loaded
  useEffect(() => {
    if (activeCourseID && activeCourseQuiz && activeTab === 4) {
      dispatch(fetchCourseV2QuizQuestionsThunk(activeCourseID))
    }
  }, [activeCourseID, activeCourseQuiz, activeTab, dispatch])

  // Fetch objectives and references once details are loaded
  useEffect(() => {
    if (activeCourseID && activeCourseDetailsStatus === 'fulfilled') {
      dispatch(fetchPlanningCourseV2Objectives(activeCourseID))
      dispatch(fetchPlanningCourseV2References(activeCourseID))
    }
  }, [activeCourseID, activeCourseDetailsStatus, dispatch])

  // Auto-switch filter when progress loads and nothing needs revision
  useEffect(() => {
    if (
      coursesProgressStatus === 'fulfilled' &&
      courseFilter === 'needs-revision' &&
      overviewStats.needsRevision === 0 &&
      overviewStats.inPlanning > 0
    ) {
      setCourseFilter('in-planning')
    }
  }, [
    coursesProgressStatus,
    courseFilter,
    overviewStats.needsRevision,
    overviewStats.inPlanning,
  ])

  // Deselect course if it leaves the filtered list
  useEffect(() => {
    if (
      activeCourseID &&
      !filteredCourses.some((c) => c.id === activeCourseID)
    ) {
      setActiveCourseID(undefined)
    }
  }, [filteredCourses, activeCourseID])

  let activeCourseProgress = activeCourseID
    ? coursesProgress[activeCourseID]
    : undefined
  let legacyTitleScreen = activeCourseDetails?.legacyTitleScreens?.[0]
  let isDetailsLoading =
    activeCourseDetailsStatus === 'pending' && !activeCourseDetails

  // Per-tab completion percentages (mirrors planning-dashboard.tsx pattern)
  let detailsCompletionPct = activeCourseProgress
    ? (() => {
        let fields = [
          activeCourseDetails?.name,
          activeCourseDetails?.length,
          activeCourseDetails?.sectionID,
        ]
        let done = fields.filter(
          (f) => f !== undefined && f !== '' && f !== 0,
        ).length
        return Math.round((done / fields.length) * 100)
      })()
    : 0

  let syllabusCompletionPct = activeCourseProgress
    ? (() => {
        let fields = [
          activeCourseDetails?.description,
          activeCourseDetails?.practiceGap,
          activeCourseDetails?.practiceGapEvidence,
          activeCourseDetails?.learnerEngagement,
          activeCourseDetails?.learningOutcomes,
        ]
        let done = fields.filter((f) => f && String(f).trim().length > 0).length
        return Math.round((done / fields.length) * 100)
      })()
    : 0

  let accreditationCompletionPct = activeCourseID
    ? (() => {
        let items = [
          !!activeCourseCapce,
          !!activeCourseAncc,
          !!activeCourseDetails?.templateID,
          (activeCourseDetails?.instructors?.length ?? 0) > 0,
        ]
        let done = items.filter(Boolean).length
        return Math.round((done / items.length) * 100)
      })()
    : 0

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
            Enduring Courses (F3)
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Plan and develop self-paced CAPCE-accredited courses through the
            compliance pipeline.
          </Typography>
        </Box>
        <BackfillVideoLengthsButton />
      </Box>
      {/* Overview stats */}
      <Card variant="outlined" sx={{ mb: 3, mt: 2, p: 3 }} component="aside">
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Overview
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {coursesProgressStatus === 'pending' ||
        coursesProgressStatus === 'uninitialized' ? (
          <Box sx={{ display: 'flex', gap: 4 }}>
            {[...Array(5)].map((_, i) => (
              <Box key={i}>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 40,
                    height: 30,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 100,
                    height: 20,
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', columnGap: 4, rowGap: 1, flexWrap: 'wrap' }}
          >
            <Box>
              <Typography
                color="error.main"
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                }}
              >
                {overviewStats.needsRevision}
              </Typography>
              <Typography variant="subtitle1">Needs Revision</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.planning,
                }}
              >
                {overviewStats.inPlanning}
              </Typography>
              <Typography variant="subtitle1">In Planning</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.compliance,
                }}
              >
                {overviewStats.readyForReview}
              </Typography>
              <Typography variant="subtitle1">Ready for Review</Typography>
            </Box>
            <Box>
              <Typography
                color="success.main"
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                }}
              >
                {overviewStats.approved}
              </Typography>
              <Typography variant="subtitle1">Approved</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.planning,
                }}
              >
                {overviewStats.totalFieldsRemaining}
              </Typography>
              <Typography variant="subtitle1">Fields Remaining</Typography>
            </Box>
          </Box>
        )}
      </Card>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography variant="h5" component="h2">
            Courses
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            New Course
          </Button>
        </Box>
        <PipelineFilterBar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search courses…"
          status={courseFilter}
          onStatusChange={setCourseFilter}
          statusCounts={{
            needsRevision: overviewStats.needsRevision,
            inPlanning: overviewStats.inPlanning,
            readyForReview: overviewStats.readyForReview,
            approved: overviewStats.approved,
            all: courses.length,
          }}
          migration={migrationFilter}
          onMigrationChange={setMigrationFilter}
          migrationCounts={{
            all: courses.length,
            needsMigration: courses.filter((c) => c.publishedVersion === 1)
              .length,
            v2: courses.filter((c) => c.publishedVersion === 2).length,
          }}
          sort={sortDir}
          onSortChange={setSortDir}
        />
      </Paper>
      <Box className="grid grid-cols-1 md:grid-cols-[350px_1fr] gap-6 items-start">
        {/* Left panel — course list */}
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 32px)' },
            overflowY: { md: 'auto' },
          }}
        >
          <Paper
            sx={{ p: 3 }}
            component="nav"
            aria-label="Enduring courses list"
          >
            <CourseListWithProgress
              courses={filteredCourses}
              coursesProgress={coursesProgress}
              activeCourseID={activeCourseID}
              onSelectCourse={(id) => {
                setActiveCourseID(id)
                setActiveTab(0)
              }}
              isLoading={coursesStatus === 'pending'}
              filterType={courseFilter}
              searchQuery={searchQuery}
            />
          </Paper>
        </Box>

        {/* Right panel — course detail */}
        <Box
          role="main"
          aria-label="Course planning details"
          sx={{ minWidth: 0 }}
        >
          {!activeCourseID && <SelectItemHint type="course" />}

          {activeCourseID && activeCourseDetails && (
            <Box>
              {/* Course header */}
              <Card variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {activeCourseDetails.name}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {activeCourseDetails.published === 'published' && (
                    <Chip
                      label={`v${activeCourseDetails.publishedVersion} live`}
                      size="small"
                      color="success"
                    />
                  )}
                  {activeCourseDetails.version >
                    activeCourseDetails.publishedVersion && (
                    <Chip
                      label={`v${activeCourseDetails.version} draft`}
                      size="small"
                      color="default"
                    />
                  )}
                  {activeCourseDetails.published !== 'published' &&
                    activeCourseDetails.version ===
                      activeCourseDetails.publishedVersion && (
                      <Chip
                        label={`v${activeCourseDetails.version} draft`}
                        size="small"
                        color="default"
                      />
                    )}
                </Box>

                {activeCourseProgress && (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                          }}
                        >
                          Data Entry
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {Math.round(
                            activeCourseProgress.dataCompleteness
                              .percentComplete ?? 0,
                          )}
                          %
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={
                          activeCourseProgress.dataCompleteness
                            .percentComplete ?? 0
                        }
                        sx={{
                          height: 6,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor:
                              theme.palette.contentPipelineColors.planning,
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                          }}
                        >
                          Compliance
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {Math.round(
                            activeCourseProgress.complianceStatus
                              .percentApproved ?? 0,
                          )}
                          %
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={
                          activeCourseProgress.complianceStatus
                            .percentApproved ?? 0
                        }
                        sx={{
                          height: 6,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor:
                              theme.palette.contentPipelineColors.compliance,
                          },
                        }}
                      />
                    </Box>

                    {(activeCourseProgress.complianceStatus.rejected ?? 0) >
                      0 && (
                      <Alert severity="error" sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                          }}
                        >
                          {activeCourseProgress.complianceStatus.rejected} field
                          {activeCourseProgress.complianceStatus.rejected === 1
                            ? ''
                            : 's'}{' '}
                          rejected
                        </Typography>
                        <Typography variant="body2">
                          {activeCourseProgress.complianceStatus.rejectedFields?.join(
                            ', ',
                          )}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
              </Card>

              <Box sx={{ mt: 2 }}>
                <CoursePublishChecklist
                  courseID={activeCourseID}
                  publishedStatus={activeCourseDetails.published}
                />
              </Box>

              {/* Progress cards — click to switch tab */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 2,
                  mt: 3,
                  mb: 0,
                }}
                role="group"
                aria-label="Section completion overview"
              >
                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 0
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(0)}
                  aria-pressed={activeTab === 0}
                  aria-label={`Course Details — ${detailsCompletionPct}% complete`}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: theme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {detailsCompletionPct > 0
                      ? `${detailsCompletionPct}%`
                      : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Course Details
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 3
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(3)}
                  aria-pressed={activeTab === 3}
                  aria-label={`Accreditation & Setup — ${
                    accreditationCompletionPct > 0
                      ? `${accreditationCompletionPct}% complete`
                      : 'Not started'
                  }`}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: theme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {accreditationCompletionPct > 0
                      ? `${accreditationCompletionPct}%`
                      : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Accreditation
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 1
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(1)}
                  aria-pressed={activeTab === 1}
                  aria-label={`Syllabus — ${syllabusCompletionPct > 0 ? `${syllabusCompletionPct}% complete` : 'Not started'}`}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: theme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {syllabusCompletionPct > 0
                      ? `${syllabusCompletionPct}%`
                      : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Syllabus
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 2
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(2)}
                  aria-pressed={activeTab === 2}
                  aria-label="Content"
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: theme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {(() => {
                      let unmigrated =
                        (activeMigratableContent?.videos?.filter(
                          (v) => !v.isMigrated,
                        ).length ?? 0) +
                        (activeMigratableContent?.showNotes?.filter(
                          (sn) => !sn.isMigrated,
                        ).length ?? 0) +
                        (activeCourseDetails.legacyTextContent?.length ?? 0)
                      if (unmigrated > 0) return `⚠ ${unmigrated}`
                      if (
                        activeCourseVideos.length > 0 ||
                        activeCourseShowNotes.length > 0
                      )
                        return '100%'
                      return '—'
                    })()}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Content
                  </Typography>
                </Card>

                <Card
                  elevation={0}
                  component="button"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor:
                      activeTab === 4
                        ? 'action.selected'
                        : 'background.paperDark',
                    '&:hover': { bgcolor: 'action.hover' },
                    border: 'none',
                  }}
                  onClick={() => setActiveTab(4)}
                  aria-pressed={activeTab === 4}
                  aria-label={`Assessment — ${activeMigratableContent?.quizMigrated ? '100% complete' : 'Not started'}`}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 0.5,
                      color: theme.palette.contentPipelineColors.planning,
                    }}
                  >
                    {activeMigratableContent?.quizMigrated ? '100%' : '—'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Assessment
                  </Typography>
                </Card>
              </Box>

              {/* Tab bar */}
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ mb: 3 }}
                aria-label="Course planning section tabs"
              >
                <Tab
                  label="Course Details"
                  value={0}
                  id="tab-0"
                  aria-controls="tabpanel-0"
                />
                <Tab
                  label="Accreditation & Setup"
                  value={3}
                  id="tab-3"
                  aria-controls="tabpanel-3"
                />
                <Tab
                  label="Syllabus"
                  value={1}
                  id="tab-1"
                  aria-controls="tabpanel-1"
                />
                <Tab
                  label="Content"
                  value={2}
                  id="tab-2"
                  aria-controls="tabpanel-2"
                />
                <Tab
                  label="Assessment"
                  value={4}
                  id="tab-4"
                  aria-controls="tabpanel-4"
                />
              </Tabs>

              {isDetailsLoading ? (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                >
                  {activeTab === 0 ? (
                    <>
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 56,
                          borderRadius: 1,
                        }}
                      />
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 2,
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          sx={{
                            height: 56,
                            borderRadius: 1,
                          }}
                        />
                        <Skeleton
                          variant="rectangular"
                          sx={{
                            height: 56,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 56,
                          borderRadius: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 80,
                          borderRadius: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 80,
                          borderRadius: 1,
                        }}
                      />
                    </>
                  ) : activeTab === 2 ? (
                    <>
                      {[...Array(3)].map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="rectangular"
                          sx={{
                            height: 64,
                            borderRadius: 1,
                          }}
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="rectangular"
                          sx={{
                            height: 88,
                            borderRadius: 1,
                          }}
                        />
                      ))}
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 120,
                          borderRadius: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 1,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          height: 120,
                          borderRadius: 1,
                        }}
                      />
                    </>
                  )}
                </Box>
              ) : (
                <>
                  {/* Tab 0 — Course Details */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-0"
                    aria-labelledby="tab-0"
                    hidden={activeTab !== 0}
                  >
                    {activeTab === 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2.5,
                        }}
                      >
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="name"
                          fieldLabel="Course Name"
                          fieldType="text"
                          complianceStatus={activeCourseCompliance?.name}
                          complianceFeedback={
                            activeCourseCompliance?.nameFeedback
                          }
                          required
                        />

                        <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <CourseField
                            courseID={activeCourseID}
                            course={activeCourseDetails}
                            fieldName="length"
                            fieldLabel="CE Hours"
                            fieldType="number"
                            complianceStatus={activeCourseCompliance?.hours}
                            complianceFeedback={
                              activeCourseCompliance?.hoursFeedback
                            }
                            required
                          />
                          <CourseField
                            courseID={activeCourseID}
                            course={activeCourseDetails}
                            fieldName="sectionID"
                            fieldLabel="Section (Category)"
                            fieldType="select"
                            complianceStatus={activeCourseCompliance?.section}
                            complianceFeedback={
                              activeCourseCompliance?.sectionFeedback
                            }
                            required
                          />
                        </Box>

                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="thumbnail"
                          fieldLabel="Thumbnail Image"
                          fieldType="image"
                          complianceStatus={activeCourseCompliance?.thumbnail}
                          complianceFeedback={
                            activeCourseCompliance?.thumbnailFeedback
                          }
                        />
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="banner"
                          fieldLabel="Banner Image"
                          fieldType="image"
                          complianceStatus={activeCourseCompliance?.banner}
                          complianceFeedback={
                            activeCourseCompliance?.bannerFeedback
                          }
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Tab 2 — Content */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-2"
                    aria-labelledby="tab-2"
                    hidden={activeTab !== 2}
                  >
                    {activeTab === 2 && (
                      <CourseContentTab
                        courseID={activeCourseID}
                        courseSteps={activeCourseDetails?.courseSteps ?? []}
                        complianceStatus={activeCourseCompliance?.content}
                        complianceFeedback={
                          activeCourseCompliance?.contentFeedback
                        }
                        migratableVideos={activeMigratableContent?.videos}
                        migratableShowNotes={activeMigratableContent?.showNotes}
                      />
                    )}
                  </Box>

                  {/* Tab 1 — Syllabus */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-1"
                    aria-labelledby="tab-1"
                    hidden={activeTab !== 1}
                  >
                    {activeTab === 1 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2.5,
                        }}
                      >
                        {legacyTitleScreen && (
                          <Alert
                            severity="warning"
                            action={
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() => setTitleScreenModalOpen(true)}
                              >
                                View Title Screen
                              </Button>
                            }
                          >
                            This course has a legacy Title Screen. Copy its
                            content into the Syllabus fields below, then it will
                            be replaced when the course migrates to v2.
                          </Alert>
                        )}
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="description"
                          fieldLabel="Description"
                          fieldType="textarea"
                          complianceStatus={activeCourseCompliance?.description}
                          complianceFeedback={
                            activeCourseCompliance?.descriptionFeedback
                          }
                          required
                        />
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="practiceGap"
                          fieldLabel="Practice Gap"
                          fieldType="textarea"
                          complianceStatus={activeCourseCompliance?.practiceGap}
                          complianceFeedback={
                            activeCourseCompliance?.practiceGapFeedback
                          }
                        />
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="practiceGapEvidence"
                          fieldLabel="Practice Gap Evidence"
                          fieldType="textarea"
                          complianceStatus={
                            activeCourseCompliance?.practiceGapEvidence
                          }
                          complianceFeedback={
                            activeCourseCompliance?.practiceGapEvidenceFeedback
                          }
                        />
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="learnerEngagement"
                          fieldLabel="Learner Engagement"
                          fieldType="textarea"
                          complianceStatus={
                            activeCourseCompliance?.learnerEngagement
                          }
                          complianceFeedback={
                            activeCourseCompliance?.learnerEngagementFeedback
                          }
                        />
                        <CourseField
                          courseID={activeCourseID}
                          course={activeCourseDetails}
                          fieldName="learningOutcomes"
                          fieldLabel="Learning Outcomes"
                          fieldType="textarea"
                          complianceStatus={
                            activeCourseCompliance?.learningOutcomes
                          }
                          complianceFeedback={
                            activeCourseCompliance?.learningOutcomesFeedback
                          }
                        />

                        <Divider />

                        <CourseObjectivesManagement
                          courseID={activeCourseID}
                          objectives={activeCourseDetails.objectives ?? []}
                          complianceStatus={activeCourseCompliance?.objectives}
                          complianceFeedback={
                            activeCourseCompliance?.objectivesFeedback
                          }
                        />

                        <Divider />

                        <CourseReferencesManagement
                          courseID={activeCourseID}
                          references={activeCourseDetails.references ?? []}
                          complianceStatus={activeCourseCompliance?.references}
                          complianceFeedback={
                            activeCourseCompliance?.referencesFeedback
                          }
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Tab 3 — Accreditation & Setup */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-3"
                    aria-labelledby="tab-3"
                    hidden={activeTab !== 3}
                  >
                    {activeTab === 3 && (
                      <Box>
                        <Typography
                          color="text.secondary"
                          variant="body2"
                          sx={{ mb: 3 }}
                        >
                          Configure CAPCE accreditation, ANCC accreditation, and
                          instructors for this course.
                        </Typography>

                        {/* CAPCE */}
                        {activeCourseCapceStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1, mb: 3 }}
                          />
                        ) : activeMigratableContent?.capceExists &&
                          !activeMigratableContent?.capceMigrated ? (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                              mb: 3,
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              CAPCE Not Yet Migrated
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mb: 2 }}
                            >
                              A legacy CAPCE record exists. Migrate it to v2 to
                              continue.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<UpgradeIcon />}
                              onClick={() =>
                                setMigrateAccreditationModalOpen(true)
                              }
                              aria-label="Migrate CAPCE accreditation to v2"
                            >
                              Migrate CAPCE to v2
                            </Button>
                          </Box>
                        ) : activeCourseCapce ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 3,
                              mb: 6,
                              p: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                            }}
                          >
                            <Typography
                              color="primary.main"
                              variant="h6"
                              component="h4"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              CAPCE Accreditation Details
                            </Typography>

                            {activeCourseCompliance?.capce && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 2,
                                  bgcolor: 'background.paperDark',
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  CAPCE Accreditation Status
                                </Typography>
                                <Chip
                                  label={
                                    activeCourseCompliance.capce === 'approved'
                                      ? 'Approved'
                                      : activeCourseCompliance.capce ===
                                          'rejected'
                                        ? 'Rejected'
                                        : activeCourseCompliance.capce ===
                                            'updated'
                                          ? 'Updated - Pending Review'
                                          : activeCourseCompliance.capce ===
                                              'unapproved'
                                            ? 'Unapproved'
                                            : 'Pending Review'
                                  }
                                  color={
                                    activeCourseCompliance.capce === 'approved'
                                      ? 'success'
                                      : activeCourseCompliance.capce ===
                                            'rejected' ||
                                          activeCourseCompliance.capce ===
                                            'unapproved'
                                        ? 'error'
                                        : 'primary'
                                  }
                                />
                              </Box>
                            )}

                            <CourseCAPCEFieldEditable
                              courseID={activeCourseID}
                              accreditation={activeCourseCapce}
                              fieldName="courseCode"
                              fieldLabel="Course Code"
                              fieldType="text"
                              complianceStatus={activeCourseCompliance?.capce}
                              required
                            />

                            <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="topicArea"
                                fieldLabel="Topic Area"
                                fieldType="select"
                                complianceStatus={activeCourseCompliance?.capce}
                                required
                              />
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="classType"
                                fieldLabel="Class Type"
                                fieldType="select"
                                complianceStatus={activeCourseCompliance?.capce}
                                required
                              />
                            </Box>

                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 'medium', mt: 1 }}
                            >
                              Important Dates
                            </Typography>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(3, 1fr)',
                                },
                                gap: 3,
                              }}
                            >
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="applicationDate"
                                fieldLabel="Application Date"
                                fieldType="date"
                                complianceStatus={activeCourseCompliance?.capce}
                              />
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="submissionDate"
                                fieldLabel="Submission Date"
                                fieldType="date"
                                complianceStatus={activeCourseCompliance?.capce}
                              />
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="expirationDate"
                                fieldLabel="Expiration Date"
                                fieldType="date"
                                complianceStatus={activeCourseCompliance?.capce}
                                required
                              />
                            </Box>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(2, 1fr)',
                                },
                                gap: 3,
                              }}
                            >
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="allClearDate"
                                fieldLabel="All Clear Date"
                                fieldType="date"
                                complianceStatus={activeCourseCompliance?.capce}
                              />
                              <CourseCAPCEFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseCapce}
                                fieldName="goLiveDate"
                                fieldLabel="Go Live Date"
                                fieldType="date"
                                complianceStatus={activeCourseCompliance?.capce}
                                disabled
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                              mb: 3,
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              No CAPCE Accreditation
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mb: 2 }}
                            >
                              Create CAPCE accreditation for EMS continuing
                              education credit.
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={() => setCapceFormOpen(true)}
                              aria-label="Create CAPCE accreditation"
                            >
                              Create CAPCE Accreditation
                            </Button>
                          </Box>
                        )}

                        {/* ANCC */}
                        {activeCourseAnccStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1, mb: 3 }}
                          />
                        ) : activeMigratableContent?.anccExists &&
                          !activeMigratableContent?.anccMigrated ? (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                              mb: 3,
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              ANCC Not Yet Migrated
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mb: 2 }}
                            >
                              A legacy ANCC record exists. Migrate it to v2 to
                              continue.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<UpgradeIcon />}
                              onClick={() =>
                                setMigrateAccreditationModalOpen(true)
                              }
                              aria-label="Migrate ANCC accreditation to v2"
                            >
                              Migrate ANCC to v2
                            </Button>
                          </Box>
                        ) : activeCourseAncc ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 3,
                              mb: 4,
                              p: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                            }}
                          >
                            <Typography
                              color="primary.main"
                              variant="h6"
                              component="h4"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              ANCC Accreditation Details
                            </Typography>

                            {activeCourseCompliance?.ancc && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 2,
                                  bgcolor: 'background.paperDark',
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  ANCC Accreditation Status
                                </Typography>
                                <Chip
                                  label={
                                    activeCourseCompliance.ancc === 'approved'
                                      ? 'Approved'
                                      : activeCourseCompliance.ancc ===
                                          'rejected'
                                        ? 'Rejected'
                                        : activeCourseCompliance.ancc ===
                                            'updated'
                                          ? 'Updated - Pending Review'
                                          : activeCourseCompliance.ancc ===
                                              'unapproved'
                                            ? 'Unapproved'
                                            : 'Pending Review'
                                  }
                                  color={
                                    activeCourseCompliance.ancc === 'approved'
                                      ? 'success'
                                      : activeCourseCompliance.ancc ===
                                            'rejected' ||
                                          activeCourseCompliance.ancc ===
                                            'unapproved'
                                        ? 'error'
                                        : 'primary'
                                  }
                                />
                              </Box>
                            )}

                            <CourseANCCFieldEditable
                              courseID={activeCourseID}
                              accreditation={activeCourseAncc}
                              fieldName="courseCode"
                              fieldLabel="Course Code"
                              fieldType="text"
                              complianceStatus={activeCourseCompliance?.ancc}
                              required
                            />

                            <CourseANCCFieldEditable
                              courseID={activeCourseID}
                              accreditation={activeCourseAncc}
                              fieldName="expirationDate"
                              fieldLabel="Expiration Date"
                              fieldType="date"
                              complianceStatus={activeCourseCompliance?.ancc}
                              required
                            />

                            <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <CourseANCCFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseAncc}
                                fieldName="targetAudience"
                                fieldLabel="Target Audience"
                                fieldType="multiselect"
                                complianceStatus={activeCourseCompliance?.ancc}
                                required
                              />
                              <CourseANCCFieldEditable
                                courseID={activeCourseID}
                                accreditation={activeCourseAncc}
                                fieldName="educationalNeeds"
                                fieldLabel="Educational Needs"
                                fieldType="multiselect"
                                complianceStatus={activeCourseCompliance?.ancc}
                                required
                              />
                            </Box>

                            <CourseANCCFieldEditable
                              courseID={activeCourseID}
                              accreditation={activeCourseAncc}
                              fieldName="competency"
                              fieldLabel="Competency"
                              fieldType="textarea"
                              complianceStatus={activeCourseCompliance?.ancc}
                            />

                            <CourseANCCFieldEditable
                              courseID={activeCourseID}
                              accreditation={activeCourseAncc}
                              fieldName="professionalSource"
                              fieldLabel="Professional Source"
                              fieldType="textarea"
                              complianceStatus={activeCourseCompliance?.ancc}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                              mb: 3,
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              No ANCC Accreditation
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mb: 2 }}
                            >
                              Create ANCC accreditation for nursing continuing
                              education credit.
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={() => setAnccFormOpen(true)}
                              aria-label="Create ANCC accreditation"
                            >
                              Create ANCC Accreditation
                            </Button>
                          </Box>
                        )}
                        <Box sx={{ mb: 4 }}>
                          <CourseField
                            courseID={activeCourseID}
                            course={activeCourseDetails}
                            fieldName="templateID"
                            fieldLabel="Template"
                            fieldType="select"
                            complianceStatus={activeCourseCompliance?.template}
                            complianceFeedback={
                              activeCourseCompliance?.templateFeedback
                            }
                            required
                          />
                        </Box>
                        <Box sx={{ mb: 4 }}>
                          <CourseInstructorsManagement
                            courseID={activeCourseID}
                            instructors={activeCourseDetails.instructors ?? []}
                            complianceStatus={
                              activeCourseCompliance?.instructors
                            }
                            complianceFeedback={
                              activeCourseCompliance?.instructorsFeedback
                            }
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Tab 4 — Assessment */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-4"
                    aria-labelledby="tab-4"
                    hidden={activeTab !== 4}
                  >
                    {activeTab === 4 && (
                      <Box>
                        <Typography
                          color="text.secondary"
                          variant="body2"
                          sx={{ mb: 3 }}
                        >
                          Create and manage quiz questions to assess student
                          knowledge.
                        </Typography>

                        {activeCourseCompliance?.quiz && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 3,
                              p: 2,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 'medium' }}
                            >
                              Quiz Compliance Status
                            </Typography>
                            <Chip
                              label={
                                activeCourseCompliance.quiz === 'approved'
                                  ? 'Approved'
                                  : activeCourseCompliance.quiz === 'rejected'
                                    ? 'Rejected'
                                    : activeCourseCompliance.quiz === 'updated'
                                      ? 'Updated - Pending Review'
                                      : activeCourseCompliance.quiz ===
                                          'unapproved'
                                        ? 'Unapproved'
                                        : 'Pending Review'
                              }
                              color={
                                activeCourseCompliance.quiz === 'approved'
                                  ? 'success'
                                  : activeCourseCompliance.quiz ===
                                        'rejected' ||
                                      activeCourseCompliance.quiz ===
                                        'unapproved'
                                    ? 'error'
                                    : 'primary'
                              }
                            />
                          </Box>
                        )}

                        {activeCourseQuizStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1 }}
                          />
                        ) : activeMigratableContent?.quizExists &&
                          !activeMigratableContent.quizMigrated ? (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                              mb: 3,
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Quiz Not Yet Migrated
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mb: 2 }}
                            >
                              A legacy quiz exists for this course. Migrate it
                              to v2 to continue.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<UpgradeIcon />}
                              onClick={() => setMigrateQuizModalOpen(true)}
                              aria-label="Migrate quiz to v2"
                            >
                              Migrate Quiz to v2
                            </Button>
                          </Box>
                        ) : activeCourseQuiz ? (
                          <QuizBuilder
                            quiz={activeCourseQuiz}
                            fetchQuiz={fetchCourseV2QuizThunk}
                            fetchQuizBy={activeCourseID}
                            questionData={activeCourseQuizQuestions || []}
                            fetchQuestionData={fetchCourseV2QuizQuestionsThunk}
                            apiPath="/api/pipeline/planning/courses"
                            submitButton={
                              <Box
                                sx={{
                                  mt: 5,
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: 1,
                                }}
                              >
                                <Button
                                  color="secondary"
                                  type="submit"
                                  variant="contained"
                                  aria-label="Save quiz changes"
                                >
                                  Save Changes
                                </Button>
                              </Box>
                            }
                          />
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                              mb: 3,
                            }}
                          >
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              No Quiz Created
                            </Typography>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mb: 2 }}
                            >
                              Create a quiz to assess student knowledge at the
                              end of this course.
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={() =>
                                activeCourseID &&
                                dispatch(
                                  createCourseV2QuizThunk({
                                    courseID: activeCourseID,
                                    passingScore: 80,
                                    displayedQuestions: 10,
                                    randomizeQuestions: true,
                                  }),
                                )
                              }
                              aria-label="Create new quiz"
                            >
                              Create Quiz
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {legacyTitleScreen && (
        <MigrateTitleScreenModal
          isOpen={titleScreenModalOpen}
          onClose={() => setTitleScreenModalOpen(false)}
          titleScreen={legacyTitleScreen}
        />
      )}
      {activeCourseID && (
        <MigrateQuizModal
          isOpen={migrateQuizModalOpen}
          onClose={() => setMigrateQuizModalOpen(false)}
          courseID={activeCourseID}
        />
      )}
      {activeCourseID && (
        <MigrateAccreditationModal
          isOpen={migrateAccreditationModalOpen}
          onClose={() => setMigrateAccreditationModalOpen(false)}
          courseID={activeCourseID}
          hasCapce={activeMigratableContent?.capceExists ?? false}
          hasAncc={activeMigratableContent?.anccExists ?? false}
        />
      )}
      {activeCourseID && (
        <CourseCAPCECreationForm
          open={capceFormOpen}
          onClose={() => setCapceFormOpen(false)}
          courseID={activeCourseID}
          courseName={activeCourseDetails?.name}
        />
      )}
      {activeCourseID && (
        <CourseANCCCreationForm
          open={anccFormOpen}
          onClose={() => setAnccFormOpen(false)}
          courseID={activeCourseID}
          courseName={activeCourseDetails?.name}
        />
      )}
      <CourseCreationModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(id) => {
          setCreateModalOpen(false)
          setActiveCourseID(id)
          setActiveTab(0)
        }}
      />
    </Box>
  )
}
